import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PullRequest, MergeQueueData } from './types';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { REPO_CONFIG, formatDate, formatDuration, formatFullDateTime } from './utils';
import './MergedPRs.css';

type DatePreset = '7' | '30' | '60' | '90' | 'custom';
type SortColumn = 'merged_at' | 'mq_duration' | 'mq_attempts';
type SortDir = 'asc' | 'desc';

export const MergedPRs: React.FC = () => {
  const [allPRs, setAllPRs] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form inputs (what the user is typing)
  const [prNumberInput, setPrNumberInput] = useState('');
  const [chgTicketInput, setChgTicketInput] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('7');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const getToken = useAccessToken();
  const today = new Date().toISOString().split('T')[0];

  // Track the last loaded date range for the result label
  const lastDaysRef = useRef<number>(7);
  const lastRangeLabelRef = useRef<string>('last 7 days');

  const fetchData = useCallback(async (days: number, rangeLabel: string) => {
    setLoading(true);
    setError(null);
    lastDaysRef.current = days;
    lastRangeLabelRef.current = rangeLabel;
    try {
      const data = await fetchApi<MergeQueueData>(
        `/api/mergequeue?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}&days=${days}&enrich=true`,
        getToken
      );
      setAllPRs(data.recently_merged || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load merged PRs');
      setAllPRs([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Initial load: default 1-week window, no filters applied
  useEffect(() => {
    fetchData(7, 'last 7 days');
  }, [fetchData]);

  const fetchCustomRange = (from: string, to: string) => {
    const now = new Date();
    const days = Math.min(90, Math.max(1, Math.ceil((now.getTime() - new Date(from).getTime()) / 86400000)));
    fetchData(days, `${from} → ${to}`);
  };

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDir('desc');
    }
  };

  const filteredPRs = useMemo(() => allPRs.filter(pr => {
    if (prNumberInput.trim() && !(pr.number?.toString() ?? '').includes(prNumberInput.trim())) return false;
    if (chgTicketInput.trim() && !(pr.chg_ticket || '').toLowerCase().includes(chgTicketInput.toLowerCase().trim())) return false;
    if (datePreset === 'custom' && customTo && pr.merged_at) {
      const toDate = new Date(customTo);
      toDate.setHours(23, 59, 59, 999);
      if (new Date(pr.merged_at) > toDate) return false;
    }
    return true;
  }), [allPRs, prNumberInput, chgTicketInput, datePreset, customTo]);

  const sortedPRs = useMemo(() => {
    if (!sortColumn) return filteredPRs;
    return [...filteredPRs].sort((a, b) => {
      let aVal: number | null = null;
      let bVal: number | null = null;
      if (sortColumn === 'merged_at') {
        aVal = a.merged_at ? new Date(a.merged_at).getTime() : null;
        bVal = b.merged_at ? new Date(b.merged_at).getTime() : null;
      } else if (sortColumn === 'mq_duration') {
        aVal = a.mq_first_queued_at && a.merged_at
          ? new Date(a.merged_at).getTime() - new Date(a.mq_first_queued_at).getTime()
          : null;
        bVal = b.mq_first_queued_at && b.merged_at
          ? new Date(b.merged_at).getTime() - new Date(b.mq_first_queued_at).getTime()
          : null;
      } else {
        aVal = a.mq_attempts ?? null;
        bVal = b.mq_attempts ?? null;
      }
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredPRs, sortColumn, sortDir]);

  const resultLabel = loading
    ? 'Loading...'
    : `${sortedPRs.length} Merged PR${sortedPRs.length !== 1 ? 's' : ''} (${lastRangeLabelRef.current})`;

  const sortIcon = (col: SortColumn) => {
    if (sortColumn !== col) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="merged-prs-container">
      {/* Single-row search form */}
      <div className="merged-prs-search">
        <div className="search-form-row">
          <div className="search-field">
            <label className="search-label">PR Number</label>
            <input
              type="text"
              className="search-input"
              placeholder="e.g. 106372"
              value={prNumberInput}
              onChange={e => setPrNumberInput(e.target.value)}
            />
          </div>
          <div className="search-field">
            <label className="search-label">CHG Ticket</label>
            <input
              type="text"
              className="search-input"
              placeholder="e.g. CHG-19976"
              value={chgTicketInput}
              onChange={e => setChgTicketInput(e.target.value)}
            />
          </div>
          <div className="search-field search-field--narrow">
            <label className="search-label">Date Range</label>
            <select
              className="search-select"
              value={datePreset}
              onChange={e => {
                const value = e.target.value as DatePreset;
                setDatePreset(value);
                if (value !== 'custom') {
                  const d = parseInt(value, 10);
                  fetchData(d, d === 7 ? 'last 7 days' : `last ${d} days`);
                }
              }}
              disabled={loading}
            >
              <option value="7">1 Week ago</option>
              <option value="30">30 Days ago</option>
              <option value="60">60 Days ago</option>
              <option value="90">90 Days ago</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {datePreset === 'custom' && (
            <>
              <div className="search-field search-field--date">
                <label className="search-label">From</label>
                <input
                  type="date"
                  className="search-input"
                  value={customFrom}
                  max={customTo || today}
                  onChange={e => {
                    const newFrom = e.target.value;
                    setCustomFrom(newFrom);
                    if (newFrom && customTo) fetchCustomRange(newFrom, customTo);
                  }}
                />
              </div>
              <div className="search-field search-field--date">
                <label className="search-label">To</label>
                <input
                  type="date"
                  className="search-input"
                  value={customTo}
                  min={customFrom || undefined}
                  max={today}
                  onChange={e => {
                    const newTo = e.target.value;
                    setCustomTo(newTo);
                    if (customFrom && newTo) fetchCustomRange(customFrom, newTo);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results summary */}
      <div className="merged-prs-results-header">
        <span className="merged-prs-count">
          <span className="check-icon">✅</span> {resultLabel}
        </span>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">❌ {error}</p>
        </div>
      )}

      <aside className="mq-legend">
        <span className="mq-legend-icon">⏱️</span>
        <span>
          <strong>Time in MQ</strong> is the elapsed time from when the PR was first enqueued to when it was successfully merged.
          If a PR was evicted and re-queued, the clock starts from the <em>first</em> enqueue — so this value includes all retry attempts.
        </span>
      </aside>

      <div className="queue-table-container">
        <table className="queue-table">
          <colgroup>
            <col />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="col-pr">Pull Request</th>
              <th className="col-chg-ticket">CHG Ticket</th>
              <th className="col-author">Author</th>
              <th className="col-merged-at sortable" onClick={() => handleSort('merged_at')}>
                Merged On {sortIcon('merged_at')}
              </th>
              <th className="col-mq-duration sortable" onClick={() => handleSort('mq_duration')}>
                Time in MQ {sortIcon('mq_duration')}
              </th>
              <th className="col-mq-attempts sortable" onClick={() => handleSort('mq_attempts')}>
                MQ Attempts {sortIcon('mq_attempts')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="empty-queue-row">
                  <div className="empty-queue-message">Loading...</div>
                </td>
              </tr>
            ) : sortedPRs.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-queue-row">
                  <div className="empty-queue-message">No merged PRs found</div>
                </td>
              </tr>
            ) : (
              sortedPRs.map(pr => (
                <tr key={pr.number}>
                  <td className="col-pr">
                    <a href={pr.url} target="_blank" rel="noopener noreferrer" className="pr-link">
                      {pr.title} <span className="pr-number-muted">(#{pr.number})</span>
                    </a>
                  </td>
                  <td className="col-chg-ticket">
                    {pr.chg_ticket ? (
                      <a
                        href={`https://teladochealth.atlassian.net/browse/${pr.chg_ticket}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pr.chg_ticket}
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="col-author">
                    <div className="author-cell">
                      <img src={pr.author_avatar} alt={pr.author} className="author-avatar" />
                      <span className="author-name">{pr.author}</span>
                    </div>
                  </td>
                  <td className="col-merged-at">
                    {pr.merged_at ? (
                      <span
                        className="timestamp-rel"
                        data-full={formatFullDateTime(pr.merged_at)}
                        onMouseMove={e => {
                          e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                          e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                        }}
                      >
                        {formatDate(pr.merged_at)}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="col-mq-duration">
                    {pr.mq_first_queued_at && pr.merged_at ? (
                      <span className="enqueue-time">{formatDuration(pr.mq_first_queued_at, pr.merged_at)}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="col-mq-attempts">
                    {pr.mq_attempts != null ? (
                      <span className={`mq-attempts-badge ${pr.mq_attempts > 1 ? 'mq-attempts-retry' : ''}`}>
                        {pr.mq_attempts}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
